import React, { useRef, useState } from 'react';
import { AutocompleteField, ErrorTransitionWrapper, InputErrorText } from './shared/StyledComponents';
import PropTypes from 'prop-types';
import { CSSTransition } from 'react-transition-group';
import { useTheme } from '@mui/material/styles';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';

/**
 * FreeSoloAutocompleteField renders an Autocomplete input for selecting a site by ID and name.
 * It supports free solo input, error display, and integrates with validation refs.
 *
 * @component
 * @param {Object[]} options - Array of site objects to display as options. Each should have `id` and optionally `name`.
 * @param {function} handleChange - Callback fired when the value changes.
 * @param {function} handleBlur - Callback fired when the input loses focus.
 * @param {Object} validationRef - Ref object for form validation, expects a mutable `current` property.
 * @param {string} id - The id for the input field.
 * @param {Object} [defaultValue] - The default value object for the field, expects an `id` property.
 * @param {string} [error] - Error message to display below the field.
 * @param {string} label - Label for the input field.
 */
export default function FreeSoloAutocompleteField({
  options,
  handleChange,
  handleBlur,
  validationRef,
  id,
  defaultValue,
  error,
  label,
}) {
  const [idValue, setIdValue] = useState(defaultValue?.id ?? null);
  const [showError, setShowError] = useState(false);
  const [prevError, setPrevError] = useState(false);
  const theme = useTheme();
  const errorRef = useRef();

  if (!!error !== prevError) {
    setPrevError(!!error);
  }

  if (!!error && !!error !== prevError) {
    setShowError(true);
  }

  return (
    <AutocompleteField
      freeSolo
      defaultValue={`${defaultValue?.id}`}
      options={options}
      id={id}
      onChange={(ev, val) => {
        const newVal = val?.value || null;
        const customEvent = createCustomEvent(ev, id, newVal);
        validationRef.current[id].value = newVal;
        handleChange(customEvent);
        setIdValue(newVal);
      }}
      onBlur={(ev) => {
        validationRef.current[id].value = idValue;
        handleBlur(createCustomEvent(ev, id, idValue));
      }}
      renderInput={(params) => (
        <CSSTransition
          in={!!error}
          timeout={theme.transitions.duration.shorter}
          classNames="error-text"
          nodeRef={errorRef}
          onExited={() => {
            setShowError(false);
          }}
        >
          <FormControl fullWidth>
            <TextField
              {...params}
              fullWidth
              label={label}
              value={idValue}
              name={id}
              onChange={(ev) => {
                const newVal =
                  options.filter(
                    (option) => option.label === ev.target.value
                  )[0]?.value || ev.target.value;
                validationRef.current[id].value = newVal;
                handleChange(createCustomEvent(ev, ev.target.id, newVal));
                setIdValue(newVal);
              }}
              onBlur={(ev) => {
                validationRef.current[id].value = idValue;
                handleBlur(createCustomEvent(ev, ev.target.id, idValue));
              }}
            />
            {showError && (
              <ErrorTransitionWrapper ref={errorRef}>
                <InputErrorText>{error}</InputErrorText>
              </ErrorTransitionWrapper>
            )}
            <input
              type="hidden"
              name={id}
              ref={() =>
                (validationRef.current[id] = {
                  value: idValue,
                })
              }
            />
          </FormControl>
        </CSSTransition>
      )}
    />
  );
}

FreeSoloAutocompleteField.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
    })
  ),
  handleChange: PropTypes.func.isRequired,
  handleBlur: PropTypes.func,
  validationRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
  id: PropTypes.string.isRequired,
  defaultValue: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  error: PropTypes.string,
  label: PropTypes.string.isRequired,
};

/**
 * helpers
 */

const createCustomEvent = (event, id, value) => {
  return {
    ...event,
    preventDefault: () => event.preventDefault(),
    target: {
      ...event.target,
      id,
      value,
    },
  };
};
